import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import AssetNav from "../components/AssetNav";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import FileUpload from "../components/FileUpload";
import { toast } from "sonner";
import { useAuth, hasRole } from "../lib/auth";
import ExportButtons from "../components/ExportButtons";

export default function AssetWarranty() {
  const { user } = useAuth();
  const canEdit = hasRole(user, "admin", "hk_supervisor");
  const [units, setUnits] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [pickOpen, setPickOpen] = useState(false);
  const [pickUnitId, setPickUnitId] = useState("");

  const load = () => api.get("/assets/units").then(({ data }) => setUnits(data.filter(u => u.status !== "disposed")));
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const soon = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const flag = (end) => !end ? null : end < today ? "expired" : end <= soon ? "due" : "ok";

  const openEdit = (u) => {
    setEdit(u);
    setForm({
      warranty_start: u.warranty?.start || "", warranty_end: u.warranty?.end || "",
      amc_provider: u.amc?.provider || "", amc_start: u.amc?.start || "", amc_end: u.amc?.end || "",
      amc_ref: u.amc?.ref || "", amc_cost: u.amc?.cost || "",
    });
  };

  const save = async () => {
    try {
      await api.patch(`/assets/units/${edit.id}`, {
        warranty: { start: form.warranty_start || null, end: form.warranty_end || null },
        amc: { provider: form.amc_provider || null, start: form.amc_start || null, end: form.amc_end || null, ref: form.amc_ref || null, cost: form.amc_cost ? Number(form.amc_cost) : null },
      });
      toast.success("Warranty/AMC saved"); setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const refreshEdit = async () => {
    const { data } = await api.get("/assets/units");
    const fresh = data.find(u => u.id === edit.id);
    setUnits(data.filter(u => u.status !== "disposed"));
    if (fresh) setEdit(fresh);
  };
  const addProof = async (url, type) => {
    if (!url) return;
    try { await api.post(`/assets/units/${edit.id}/warranty-proof`, { url, type }); toast.success("Proof uploaded"); refreshEdit(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const verifyWarranty = async () => {
    try { await api.post(`/assets/units/${edit.id}/warranty-verify`); toast.success("Warranty verified"); refreshEdit(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const B = ({ s }) => s === "expired" ? <Badge className="bg-red-100 border text-red-800">Overdue/Expired</Badge>
    : s === "due" ? <Badge className="bg-amber-100 border text-amber-800">Due ≤30d</Badge>
    : s === "ok" ? <Badge className="bg-emerald-100 border text-emerald-800">OK</Badge> : <span className="text-slate-300">—</span>;

  return (
    <Layout>
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <h1 className="section-title">AMC & Warranty</h1>
        <div className="flex items-center gap-2">
          {canEdit && <Button onClick={() => { setPickUnitId(""); setPickOpen(true); }} className="h-9 bg-slate-900 text-white hover:bg-slate-800" data-testid="add-warranty-btn">Add Warranty/AMC</Button>}
          <ExportButtons endpoint="/assets/export/warranty" filenameBase="asset_warranty_amc" />
        </div>
      </div>
      <AssetNav />
      <Card className="p-0 overflow-x-auto">
        <table className="vr-table" data-testid="warranty-table">
          <thead><tr><th>Asset ID</th><th>Item</th><th>Warranty End</th><th></th><th>AMC Provider</th><th>AMC End</th><th></th>{canEdit && <th></th>}</tr></thead>
          <tbody>
            {units.map(u => (
              <tr key={u.id}>
                <td className="font-mono text-xs">{u.asset_id}</td><td>{u.item_name}</td>
                <td className="text-xs">{u.warranty?.end || "—"}</td><td><B s={flag(u.warranty?.end)} /></td>
                <td className="text-xs">{u.amc?.provider || "—"}</td>
                <td className="text-xs">{u.amc?.end || "—"}</td><td><B s={flag(u.amc?.end)} /></td>
                {canEdit && <td><Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-warranty-${u.asset_id}`}>Edit</Button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent data-testid="warranty-dialog">
          <DialogHeader><DialogTitle>{edit?.asset_id} — Warranty & AMC</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Warranty Start</Label><Input type="date" value={form.warranty_start} onChange={e => setForm({ ...form, warranty_start: e.target.value })} className="h-10 mt-1" /></div>
            <div><Label>Warranty End</Label><Input type="date" value={form.warranty_end} onChange={e => setForm({ ...form, warranty_end: e.target.value })} className="h-10 mt-1" data-testid="warranty-end-input" /></div>
            <div><Label>AMC Provider</Label><Input value={form.amc_provider} onChange={e => setForm({ ...form, amc_provider: e.target.value })} className="h-10 mt-1" /></div>
            <div><Label>AMC Contract Ref</Label><Input value={form.amc_ref} onChange={e => setForm({ ...form, amc_ref: e.target.value })} className="h-10 mt-1" /></div>
            <div><Label>AMC Start</Label><Input type="date" value={form.amc_start} onChange={e => setForm({ ...form, amc_start: e.target.value })} className="h-10 mt-1" /></div>
            <div><Label>AMC End</Label><Input type="date" value={form.amc_end} onChange={e => setForm({ ...form, amc_end: e.target.value })} className="h-10 mt-1" /></div>
            <div><Label>AMC Cost</Label><Input type="number" value={form.amc_cost} onChange={e => setForm({ ...form, amc_cost: e.target.value })} className="h-10 mt-1" /></div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-700">Warranty Proof
                {edit?.warranty_status === "verified"
                  ? <Badge className="ml-2 bg-emerald-100 border text-emerald-800">Verified</Badge>
                  : <Badge className="ml-2 bg-amber-100 border text-amber-800">Unverified</Badge>}
              </div>
            </div>
            <div className="space-y-1 mb-2" data-testid="warranty-proofs">
              {(edit?.warranty_proofs || []).map((p, i) => (
                <div key={p.url} className="text-xs flex gap-2 items-center">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} target="_blank" rel="noreferrer" className="text-blue-700 underline" data-testid={`warranty-proof-${i}`}>{p.type || "Proof"} {i + 1}</a>
                  <span className="text-slate-400">· {p.uploaded_by} · {(p.uploaded_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
              ))}
              {(!edit?.warranty_proofs || edit.warranty_proofs.length === 0) && <span className="text-xs text-slate-400">No proof uploaded (warranty card / invoice / AMC agreement).</span>}
            </div>
            <FileUpload value={null} onChange={url => addProof(url, "warranty_card")} testid="warranty-proof-upload" />
            <Button onClick={verifyWarranty} variant="outline" className="mt-2" data-testid="verify-warranty" disabled={!(edit?.warranty_proofs || []).length}>Mark Verified</Button>
          </div>
          <Button onClick={save} className="bg-slate-900 text-white hover:bg-slate-800 mt-2" data-testid="save-warranty">Save</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={pickOpen} onOpenChange={setPickOpen}>
        <DialogContent data-testid="pick-asset-dialog">
          <DialogHeader><DialogTitle>Add Warranty / AMC</DialogTitle></DialogHeader>
          <div>
            <Label>Select a tagged asset</Label>
            <Select value={pickUnitId} onValueChange={setPickUnitId}>
              <SelectTrigger className="h-11 mt-1" data-testid="pick-asset-select"><SelectValue placeholder="Choose an asset" /></SelectTrigger>
              <SelectContent>
                {units.map(u => <SelectItem key={u.id} value={u.id}>{u.asset_id} · {u.item_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-2">Pick an existing tagged asset to record its warranty and AMC. No new stock is created.</p>
          </div>
          <Button className="bg-slate-900 text-white hover:bg-slate-800 mt-2" disabled={!pickUnitId} data-testid="pick-asset-continue"
            onClick={() => { const u = units.find(x => x.id === pickUnitId); if (u) { setPickOpen(false); openEdit(u); } }}>Continue</Button>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
